import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { StatusPill } from "@/components/StatusPill";
import { useI18n } from "@/i18n/I18nContext";
import { ArrowLeft, Loader2, Bot, ShieldCheck, UserCheck, Check, X, RotateCcw } from "lucide-react";
import { toast } from "sonner";

const STEP_ICON = { AGENT_TASK: Bot, REVIEW: ShieldCheck, HUMAN_APPROVAL: UserCheck };

export default function WorkflowRun() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useI18n();
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.workflowRun(id).then(setRun), [id]);
  useEffect(() => { load(); }, [load]);

  const ACTION_LABEL = { APPROVE: t("wr.actionApprove"), REVISE: t("wr.actionRevise"), REJECT: t("wr.actionReject") };

  const decide = async (aid, action) => {
    setBusy(true);
    try { await api.decideApproval(aid, action, t("wr.notePrefix", { action })); toast.success(t("wr.actionToast", { v: ACTION_LABEL[action] || action })); await load(); }
    catch (e) { toast.error(e.response?.data?.detail || t("common.failed")); } finally { setBusy(false); }
  };

  if (!run) return <div className="flex items-center justify-center py-32"><Loader2 className="animate-spin text-[#2563EB]" /></div>;
  const pending = (run.approvals || []).find((a) => a.status === "PENDING");

  return (
    <div className="space-y-6">
      <button onClick={() => navigate("/workflows")} className="text-sm text-[#94A3B8] hover:text-white flex items-center gap-1.5" data-testid="wfrun-back"><ArrowLeft size={15} /> {t("wr.back")}</button>

      <div className="m-card p-6" style={{ background: "linear-gradient(135deg,#0F1626,#101d33)" }}>
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div><p className="mono-label">{run.code} · {run.template_code} · {run.project_code}</p><h1 className="font-display text-2xl font-extrabold text-white mt-1">{run.title}</h1><p className="text-sm text-[#94A3B8] mt-2">{run.objective}</p></div>
          <div className="text-right"><StatusPill status={run.status === "AWAITING_APPROVAL" ? "APPROVAL_REQUIRED" : run.status} label={run.status} /><p className="text-xs text-[#64748B] font-mono mt-2">cost ${run.cost}</p></div>
        </div>
      </div>

      {pending && (
        <div className="m-card p-5" style={{ borderColor: "#BE123C" }}>
          <div className="flex items-center gap-2 mb-2"><ShieldCheck size={17} className="text-[#FB7185]" /><p className="font-display font-bold text-white">{t("wr.approvalRequired")}</p><StatusPill status={pending.class} /></div>
          <p className="text-sm text-[#E2E8F0]">{pending.reason}</p>
          <div className="flex flex-wrap gap-2 mt-4">
            <button disabled={busy} onClick={() => decide(pending.id, "APPROVE")} className="btn-primary h-10 px-5 text-sm flex items-center gap-2" data-testid={`wfrun-approve-${pending.id}`}><Check size={15} /> {t("common.approve")}</button>
            <button disabled={busy} onClick={() => decide(pending.id, "REVISE")} className="btn-ghost h-10 px-5 text-sm flex items-center gap-2" data-testid={`wfrun-revise-${pending.id}`}><RotateCcw size={15} /> {t("common.revise")}</button>
            <button disabled={busy} onClick={() => decide(pending.id, "REJECT")} className="btn-ghost h-10 px-5 text-sm flex items-center gap-2" style={{ color: "#FCA5A5" }} data-testid={`wfrun-reject-${pending.id}`}><X size={15} /> {t("common.reject")}</button>
          </div>
        </div>
      )}

      <div className="relative pl-7">
        <div className="absolute left-[11px] top-3 bottom-3 w-px bg-[#1E293B]" />
        <div className="space-y-3">
          {run.steps.map((s) => {
            const Icon = STEP_ICON[s.type] || Bot;
            const done = s.status === "COMPLETED";
            return (
              <div key={s.id} className="relative" data-testid={`wf-step-${s.sequence}`}>
                <div className="absolute -left-[26px] top-3 h-5 w-5 rounded-full flex items-center justify-center border-2 border-[#070A12]" style={{ background: done ? "#10B981" : s.status === "PENDING" ? "#BE123C" : "#334155" }}>
                  <Icon size={11} className="text-white" />
                </div>
                <div className="m-card p-4">
                  <div className="flex items-center justify-between gap-2 flex-wrap">
                    <p className="text-white text-sm font-medium">{s.sequence}. {s.name} <span className="text-[10px] text-[#64748B] font-mono">· {s.type}</span></p>
                    <div className="flex items-center gap-2">{s.agent_name && <span className="text-[11px] text-[#94A3B8]">{s.agent_name}</span>}<StatusPill status={s.status === "PENDING" ? "APPROVAL_PENDING" : s.status} label={s.status} /></div>
                  </div>
                  {s.output && <details className="mt-2"><summary className="text-xs text-[#2563EB] cursor-pointer">{t("wr.viewOutput")} {s.cost ? `· $${s.cost}` : ""}</summary><p className="text-xs text-[#94A3B8] mt-2 whitespace-pre-wrap max-h-60 overflow-y-auto">{s.output}</p></details>}
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
